<script setup lang="ts">
import OrderForm from '@/components/features/orders/OrderForm.vue'
import CustomerDetailsForm from '@/components/features/orders/CustomerDetailsForm.vue'
import ProductDetailsForm from '@/components/features/orders/ProductDetailsForm.vue'
import { useRouter, RouterLink } from 'vue-router'
import { useOrderForm } from '@/composables/useOrderForm'
import { Icon } from '@iconify/vue'
import Button from 'primevue/button'
import { ref, onMounted, computed } from 'vue'
import PackingListDialogue from '@/components/features/orders/PackingListDialog.vue'
import { getRollsList } from '@/api/rolls'
import { type Customer } from '@/types/customer-types'
import { getCustomerList } from '@/api/customers'
import FormTextArea from '@/components/inputs/FormTextArea.vue'
import { cancelOrder, uncancelOrder } from '@/api/orders'
import { useToast } from 'primevue/usetoast'

const router = useRouter()
const toast = useToast()
const { values, handleSubmit, setErrors, formType, newProduct, removeProduct, productFields } =
  useOrderForm('view')
  const rollList = ref<any>([])

const orderId = router.currentRoute.value.params.id
const showAddPackingList = ref(false)
const customerList = ref<Customer[]>([])
const handleEditOrder = () => {
  router.push(`/orders/${orderId}/edit`)
}

const handleToggleCancel = async () => {
  try {
    const orderIdStr = Array.isArray(orderId) ? orderId[0] : orderId
    if (values.isCancelled) {
      await uncancelOrder(orderIdStr)
      toast.add({ severity: 'success', summary: 'Order Restored!', detail: 'The order has been successfully restored.', life: 3000 })
      values.isCancelled = false
    } else {
      await cancelOrder(orderIdStr)
      toast.add({ severity: 'success', summary: 'Order Cancelled!', detail: 'The order has been successfully cancelled.', life: 3000 })
      values.isCancelled = true
    }
  } catch (error: any) {
    const action = values.isCancelled ? 'Restore' : 'Cancel'
    toast.add({ severity: 'error', summary: `${action} Failed!`, detail: error.response?.data?.message || `Failed to ${action.toLowerCase()} order`, life: 3000 })
  }
}

const isCancelled = computed(() => values?.isCancelled === true)

const comformityOptions = [
  {
    id: '1',
    name: 'Download As PDF'
  },
  {
    id: '2',
    name: 'Download As Microsoft Word'
  }
]


onMounted(async () => {
  rollList.value = await getRollsList()
  customerList.value = await getCustomerList()
})
</script>

<template>
  <div class="flex justify-between items-center mb-10">
    <RouterLink to="/orders" class="flex items-center gap-x-2 group hover:text-primary-default">
      <Icon
        icon="material-symbols:arrow-back-rounded"
        class="text-3xl group-hover:translate-x-2 transition-transform"
      />
      <span>Back</span>
    </RouterLink>

    <h1 class="font-semibold text-2xl">Customer Order</h1>

    <div class="flex gap-2 flex-col">
      <Button
        label="Create Packing List"
        @click="
          () => {
            showAddPackingList = true
          }
        "
      >
        <template #icon>
          <Icon icon="mdi:add-to-photos" class="text-xl ml-3 order-1" />
        </template>
      </Button>
      <Button
        outlined
        class="flex items-center gap-x-2"
        @click="handleToggleCancel"
      >
        <span>{{ isCancelled ? 'Restore Order' : 'Cancel Order' }}</span>
        <Icon :icon="isCancelled ? 'material-symbols:undo' : 'material-symbols:cancel'" class="text-xl" />
      </Button>
      <Button outlined label="Edit Order" @click="handleEditOrder">
        <template #icon>
          <Icon icon="basil:edit-solid" class="text-xl ml-3 order-1" />
        </template>
      </Button>
    </div>
  </div>
  <div class="bg-white rounded-xl pb-2">
    <OrderForm :type="formType" :values="values" :onSubmit="handleSubmit" :setErrors="setErrors" />
    <CustomerDetailsForm
      :customer-list="customerList"
      :type="formType"
      :values="values"
      :onSubmit="handleSubmit"
      :setErrors="setErrors"
    />
    <p class="text-lg font-bold ml-7">Product Details ({{ productFields.length }})</p>
    <template v-for="(product, index) in productFields" :key="product.key">
      <ProductDetailsForm
        :customer-list="customerList"
        :type="formType"
        :formPath="`${index}`"
        :newProduct="newProduct"
        :productFields="values?.orderProducts[index]"
        :removeProduct="() => removeProduct(index)"
        :showRemoveButton="false"
        :values="values"
        :onSubmit="handleSubmit"
        :setErrors="setErrors"
      />
    </template>

    <PackingListDialogue
    :visible="showAddPackingList"
    :comformityOptions="comformityOptions"
    :orderedProductsOptions="values?.orderProducts"
    :rolls="rollList"
    :close="() => {showAddPackingList = false}"
    :orderNumber="values?.orderNumber"
    />
    <div class="p-5">
      <FormTextArea
        label="Delivery Information"
        inputName="deliveryInformation"
        id="deliveryInformation"
        :disabled="true"
      />
    </div>
  </div>
</template>
